import { createApi } from '@reduxjs/toolkit/query/react';

import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { API_BUSINESS_URL } from '@/utils';

import {
  CommerceSettings,
  NewCommerceSettings,
  UpdateCommerceSettings,
  CommerceSettingsFilters,
} from '../models';

const commerceSettingsService = createApi({
  reducerPath: 'commerceSettingsService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}`),
  tagTypes: ['CommerceSettings'],
  endpoints: (builder) => ({
    getCommerceSettings: builder.query<
      PaginatedResponse<CommerceSettings>,
      {
        paginated: PaginatedRequest;
        filters?: CommerceSettingsFilters;
        search?: string;
      }
    >({
      query: ({ paginated, filters, search }) => {
        const params: Record<string, any> = {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        };

        if (search && search.trim() !== '') {
          params.search = search.trim();
        } else {
          if (filters?.key) params.key = filters.key;
          if (filters?.value) params.value = filters.value;
          if (filters?.longValue) params.longValue = filters.longValue;
          if (filters?.dateValue) params.dateValue = filters.dateValue;
          if (filters?.numberValue !== undefined)
            params.numberValue = filters.numberValue;
        }

        if (filters?.commerceId) params.commerceId = filters.commerceId;

        return {
          url: '/commerce-settings',
          params,
        };
      },
      providesTags: ['CommerceSettings'],
    }),
    getCommerceSettingsById: builder.query<
      CommerceSettings,
      { commerceSettingsId: string }
    >({
      query: ({ commerceSettingsId }) => ({
        url: `/commerce-settings/${commerceSettingsId}`,
      }),
      providesTags: ['CommerceSettings'],
    }),
    createCommerceSettings: builder.mutation<
      CommerceSettings,
      NewCommerceSettings
    >({
      query: (body) => ({
        url: '/commerce-settings',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CommerceSettings'],
    }),
    updateCommerceSettings: builder.mutation<
      CommerceSettings,
      { commerceSettingsId: string; data: UpdateCommerceSettings }
    >({
      query: ({ commerceSettingsId, data }) => ({
        url: `/commerce-settings/${commerceSettingsId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['CommerceSettings'],
    }),
    deleteCommerceSettings: builder.mutation<void, string>({
      query: (commerceSettingsId) => ({
        url: `/commerce-settings/${commerceSettingsId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CommerceSettings'],
    }),
  }),
});

export default commerceSettingsService;
export const {
  useGetCommerceSettingsQuery,
  useGetCommerceSettingsByIdQuery,
  useCreateCommerceSettingsMutation,
  useUpdateCommerceSettingsMutation,
  useDeleteCommerceSettingsMutation,
} = commerceSettingsService;
